import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { burstPositive, burstGold, burstReward } from "@/lib/fx";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AvatarView } from "@/components/AvatarView";
import { useI18n } from "@/lib/i18n";

export function ActionDialog({ open, onOpenChange, classId, target, events, rewards, badges, medals = [], student, contextLabel, onDone }) {
  const [tab, setTab] = useState("positive");
  const { t } = useI18n();
  if (!target) return null;
  const positives = events.filter((e) => e.type === "positive");
  const negatives = events.filter((e) => e.type === "negative");
  const isStudent = target.type === "student";
  const scopeText = isStudent ? target.label : (target.type === "group" ? `${t("scopeGroup")} «${target.label}»` : t("scopeClass"));

  const apply = async (ev) => {
    const res = await api.applyEvent({ class_id: classId, event_id: ev.id, target_type: target.type, target_id: target.id });
    if (ev.type === "positive") burstPositive();
    toast[ev.type === "positive" ? "success" : "warning"](`${ev.emoji} ${ev.name} · ${res.affected} alumno(s)`);
    onDone(); onOpenChange(false);
  };
  const award = async (b) => {
    const res = await api.awardBadge({ class_id: classId, badge_id: b.id, target_type: target.type, target_id: target.id });
    burstGold(); toast.success(`🏅 ${b.name} · ${res.affected} alumno(s)`);
    onDone(); onOpenChange(false);
  };
  const giveMedal = async (m) => {
    const res = await api.awardMedal({ class_id: classId, medal_id: m.id, target_type: target.type, target_id: target.id });
    burstGold(); toast.success(`🏆 ${m.name} · ${res.affected} alumno(s)`);
    onDone(); onOpenChange(false);
  };
  const redeem = async (r) => {
    try {
      await api.redeemReward({ class_id: classId, reward_id: r.id, student_id: target.id });
      burstReward(); toast.success(`🎁 ${r.name} canjeada`);
      onDone(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo canjear"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            {isStudent && student && <AvatarView student={student} size={40} plain />}
            {target.label}
          </DialogTitle>
        </DialogHeader>
        <div data-testid="action-scope" className="text-xs font-bold px-3 py-1.5 rounded-full bg-muted text-muted-foreground w-fit -mt-2">{scopeText}</div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 rounded-full h-12">
            <TabsTrigger value="positive" data-testid="tab-positive" className="rounded-full font-bold text-xs sm:text-sm gap-1"><span className="text-base">✓</span><span className="hidden sm:inline">{t("positives")}</span></TabsTrigger>
            <TabsTrigger value="negative" data-testid="tab-negative" className="rounded-full font-bold text-xs sm:text-sm gap-1"><span className="text-base">✕</span><span className="hidden sm:inline">{t("negatives")}</span></TabsTrigger>
            <TabsTrigger value="badge" data-testid="tab-badge" className="rounded-full font-bold text-xs sm:text-sm gap-1"><span className="text-base">★</span><span className="hidden sm:inline">{t("badges")}</span></TabsTrigger>
            <TabsTrigger value="medal" data-testid="tab-medal" className="rounded-full font-bold text-xs sm:text-sm gap-1"><span className="text-base">❖</span><span className="hidden sm:inline">{t("medals")}</span></TabsTrigger>
            <TabsTrigger value="reward" data-testid="tab-reward" className="rounded-full font-bold text-xs sm:text-sm gap-1" disabled={!isStudent}><span className="text-base">❍</span><span className="hidden sm:inline">{t("rewards")}</span></TabsTrigger>
          </TabsList>
          <div className="mt-4 max-h-[50vh] overflow-auto scrollbar-thin">
            <TabsContent value="positive"><Grid items={positives} onClick={apply} kind="event" /></TabsContent>
            <TabsContent value="negative"><Grid items={negatives} onClick={apply} kind="event" /></TabsContent>
            <TabsContent value="badge"><Grid items={badges} onClick={award} kind="badge" /></TabsContent>
            <TabsContent value="medal"><Grid items={medals} onClick={giveMedal} kind="medal" /></TabsContent>
            <TabsContent value="reward"><Grid items={rewards.filter((r) => r.available)} onClick={redeem} kind="reward" student={student} /></TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function Grid({ items, onClick, kind, student }) {
  if (!items.length) return <div className="text-center text-muted-foreground py-8 text-sm">—</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {items.map((it) => {
        const disabled = kind === "reward" && student && student.points < it.cost;
        return (
          <button key={it.id} data-testid={`action-${kind}-${it.id}`} onClick={() => !disabled && onClick(it)} disabled={disabled}
            className="p-4 rounded-2xl border-2 text-left hover:-translate-y-1 transition-transform disabled:opacity-40 disabled:hover:translate-y-0"
            style={{ borderColor: (it.color || "#888") + "66", background: (it.color || "#888") + "12" }}>
            {kind === "medal" ? (it.image ? <img src={it.image} alt="" className="w-12 h-12 object-contain" /> : <div className="text-3xl">❖</div>) : <div className="text-3xl leading-none" style={{ color: it.color }}>{it.emoji}</div>}
            <div className="font-bold text-sm mt-2 leading-tight">{it.name}</div>
            {kind === "event" && <div className="text-xs font-mono mt-1" style={{ color: it.color }}>{it.points >= 0 ? "+" : ""}{it.points} pts</div>}
            {kind === "reward" && <div className="text-xs font-mono mt-1 text-fuchsia-500">{it.cost} pts</div>}
            {kind === "badge" && it.category && <div className="text-xs text-muted-foreground mt-1">{it.category}</div>}
          </button>
        );
      })}
    </div>
  );
}
